import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { sendTransactionalEmail } from '@/lib/email/send'

export const Route = createFileRoute('/admin/')({
  component: AdminDashboardPage,
})

interface AppointmentRow {
  id: string
  status: string
  service_label: string
  scheduled_at: string
  service_address: string
  vehicle_snapshot: string | null
  notes: string | null
  cancelled_at: string | null
  cancelled_via: string | null
  rescheduled_at: string | null
  rescheduled_via: string | null
  original_scheduled_at: string | null
  created_at: string
  customers: { name: string; email: string; phone: string } | null
}

type StatusFilter = 'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'
type SourceFilter = 'all' | 'email_link' | 'manual'

async function confirmAppointment(id: string) {
  const { error } = await supabase
    .from('appointments')
    .update({ status: 'confirmed' })
    .eq('id', id)
  if (error) throw error
}

async function markCompleted(id: string) {
  const { error } = await supabase
    .from('appointments')
    .update({ status: 'completed' })
    .eq('id', id)
  if (error) throw error
}

async function cancelAppointment(id: string) {
  const { error } = await supabase
    .from('appointments')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancelled_via: 'admin',
    })
    .eq('id', id)
  if (error) throw error
}

function fmt(ts: string | null) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  })
}

function StatusBadge({ row }: { row: AppointmentRow }) {
  if (row.status === 'cancelled') {
    return (
      <span className="inline-flex items-center rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-xs font-medium">
        Cancelled
      </span>
    )
  }
  if (row.rescheduled_at) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-xs font-medium">
        Rescheduled
      </span>
    )
  }
  const map: Record<string, string> = {
    pending: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    confirmed: 'bg-green-500/10 text-green-600 dark:text-green-400',
    completed: 'bg-muted text-muted-foreground',
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${map[row.status] ?? 'bg-muted text-muted-foreground'}`}
    >
      {row.status}
    </span>
  )
}

function ViaBadge({ via }: { via: string | null }) {
  if (!via) return <span className="text-muted-foreground">Manual</span>
  if (via === 'email_link') {
    return (
      <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium">
        Via email link
      </span>
    )
  }
  return <span className="text-muted-foreground capitalize">{via}</span>
}

function AdminDashboardPage() {
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [rows, setRows] = useState<AppointmentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')

  // Auth + admin check
  useEffect(() => {
    let mounted = true
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        navigate({ to: '/admin/login' })
        return
      }
      if (!mounted) return
      setUserEmail(session.user.email ?? null)
      const { data: roleRow } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle()
      if (!mounted) return
      setIsAdmin(!!roleRow)
      setChecking(false)
    }
    check()
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate({ to: '/admin/login' })
    })
    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [navigate])

  // Load appointments
  useEffect(() => {
    if (!isAdmin) return
    setLoading(true)
    supabase
      .from('appointments')
      .select(
        'id, status, service_label, scheduled_at, service_address, vehicle_snapshot, notes, cancelled_at, cancelled_via, rescheduled_at, rescheduled_via, original_scheduled_at, created_at, customers (name, email, phone)'
      )
      .order('scheduled_at', { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        if (error) {
          toast.error('Failed to load appointments')
          setRows([])
        } else {
          setRows((data as any) ?? [])
        }
        setLoading(false)
      })
  }, [isAdmin])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false
      if (sourceFilter === 'email_link') {
        if (r.cancelled_via !== 'email_link' && r.rescheduled_via !== 'email_link') return false
      }
      if (sourceFilter === 'manual') {
        if (r.cancelled_via === 'email_link' || r.rescheduled_via === 'email_link') return false
      }
      if (search) {
        const q = search.toLowerCase()
        const hay =
          `${r.customers?.name ?? ''} ${r.customers?.email ?? ''} ${r.customers?.phone ?? ''} ${r.service_label} ${r.service_address}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [rows, statusFilter, sourceFilter, search])

  const stats = useMemo(() => {
    const total = rows.length
    const cancelledViaEmail = rows.filter((r) => r.cancelled_via === 'email_link').length
    const rescheduledViaEmail = rows.filter((r) => r.rescheduled_via === 'email_link').length
    const upcoming = rows.filter(
      (r) => r.status !== 'cancelled' && new Date(r.scheduled_at) > new Date()
    ).length
    return { total, cancelledViaEmail, rescheduledViaEmail, upcoming }
  }, [rows])

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate({ to: '/admin/login' })
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-bold">Not authorized</h1>
          <p className="text-sm text-muted-foreground">
            Your account ({userEmail}) doesn't have admin access. Ask the site
            owner to grant your account the admin role, then refresh this page.
          </p>
          <Button variant="outline" onClick={handleSignOut}>Sign out</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
            <p className="text-xs text-muted-foreground">{userEmail}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/invoices">
              <Button variant="outline" size="sm">Invoices</Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="outline" size="sm">Users</Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleSignOut}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total bookings" value={stats.total} />
          <StatCard label="Upcoming" value={stats.upcoming} />
          <StatCard label="Cancelled via email" value={stats.cancelledViaEmail} accent="destructive" />
          <StatCard label="Rescheduled via email" value={stats.rescheduledViaEmail} accent="amber" />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end">
          <div className="flex-1">
            <Label htmlFor="search">Search</Label>
            <Input
              id="search"
              placeholder="Name, email, phone, service, address…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="statusFilter">Status</Label>
            <select
              id="statusFilter"
              className="h-10 w-full md:w-44 rounded-md border bg-background px-3 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <Label htmlFor="sourceFilter">Action source</Label>
            <select
              id="sourceFilter"
              className="h-10 w-full md:w-44 rounded-md border bg-background px-3 text-sm"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
            >
              <option value="all">All</option>
              <option value="email_link">Email link only</option>
              <option value="manual">Manual only</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Service</th>
                  <th className="text-left px-4 py-3">Scheduled</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Cancelled</th>
                  <th className="text-left px-4 py-3">Rescheduled</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      Loading appointments…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No appointments match these filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.customers?.name ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.customers?.email ?? ''}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {r.customers?.phone ?? ''}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{r.service_label}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">
                          {r.service_address}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{fmt(r.scheduled_at)}</div>
                        {r.original_scheduled_at && (
                          <div className="text-xs text-muted-foreground line-through">
                            was {fmt(r.original_scheduled_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge row={r} />
                      </td>
                      <td className="px-4 py-3">
                        {r.cancelled_at ? (
                          <div className="space-y-0.5">
                            <div className="text-xs">{fmt(r.cancelled_at)}</div>
                            <ViaBadge via={r.cancelled_via} />
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.rescheduled_at ? (
                          <div className="space-y-0.5">
                            <div className="text-xs">{fmt(r.rescheduled_at)}</div>
                            <ViaBadge via={r.rescheduled_via} />
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <ActionButtons row={r} onChanged={(updates) => {
                          setRows((prev) => prev.map((p) => p.id === r.id ? { ...p, ...updates } : p))
                        }} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string
  value: number
  accent?: 'destructive' | 'amber'
}) {
  const color =
    accent === 'destructive'
      ? 'text-destructive'
      : accent === 'amber'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-foreground'
  return (
    <div className="border rounded-xl p-4 bg-card">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  )
}

function ActionButtons({
  row,
  onChanged,
}: {
  row: AppointmentRow
  onChanged: (updates: Partial<AppointmentRow>) => void
}) {
  const [busy, setBusy] = useState<null | 'confirm' | 'complete' | 'cancel'>(null)

  if (row.status === 'cancelled' || row.status === 'completed') {
    return <span className="text-muted-foreground text-xs">—</span>
  }

  const handleConfirm = async () => {
    setBusy('confirm')
    try {
      await confirmAppointment(row.id)
      onChanged({ status: 'confirmed' })
      toast.success('Booking confirmed')
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to confirm')
    } finally {
      setBusy(null)
    }
  }

  const handleComplete = async () => {
    setBusy('complete')
    try {
      await markCompleted(row.id)
      onChanged({ status: 'completed' })
      toast.success('Marked as completed')
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to update')
    } finally {
      setBusy(null)
    }
  }

  const handleCancel = async () => {
    if (!window.confirm('Cancel this booking? The customer will be emailed a cancellation notice.')) return
    setBusy('cancel')
    try {
      await cancelAppointment(row.id)
      const now = new Date().toISOString()
      onChanged({ status: 'cancelled', cancelled_at: now, cancelled_via: 'admin' })
      toast.success('Booking cancelled')

      const customerEmail = row.customers?.email
      if (customerEmail) {
        const dt = new Date(row.scheduled_at)
        const scheduledDate = dt.toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
          timeZone: 'America/New_York',
        })
        const scheduledTime = dt.toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
        })
        try {
          await sendTransactionalEmail({
            templateName: 'customer-cancellation',
            recipientEmail: customerEmail,
            idempotencyKey: `cancellation-${row.id}`,
            templateData: {
              customerName: row.customers?.name ?? '',
              scheduledDate,
              scheduledTime,
            },
          })
          toast.success('Cancellation email sent to customer')
        } catch (err: any) {
          toast.error(`Booking cancelled, but email failed: ${err.message ?? 'unknown error'}`)
        }
      }
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to cancel')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      {row.status === 'pending' && (
        <Button size="sm" onClick={handleConfirm} disabled={busy !== null}>
          {busy === 'confirm' ? 'Confirming…' : 'Accept'}
        </Button>
      )}
      {row.status === 'confirmed' && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleComplete}
          disabled={busy !== null}
        >
          {busy === 'complete' ? 'Saving…' : 'Mark completed'}
        </Button>
      )}
      <Button
        size="sm"
        variant="destructive"
        onClick={handleCancel}
        disabled={busy !== null}
      >
        {busy === 'cancel' ? 'Cancelling…' : 'Cancel'}
      </Button>
    </div>
  )
}
